import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { partnerService, contactsService } from '../../services/partnerService';
import { supabase } from '../../lib/supabase';

// ─── Confetti Engine ──────────────────────────────────────────────────────────
const ConfettiCanvas = ({ active }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animRef = useRef(null);

  const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316', '#06b6d4', '#ec4899'];

  const spawnParticles = useCallback(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const particles = [];
    for (let i = 0; i < 120; i++) {
      particles?.push({
        x: Math.random() * canvas?.width,
        y: -10,
        vx: (Math.random() - 0.5) * 6,
        vy: Math.random() * 4 + 2,
        color: COLORS?.[Math.floor(Math.random() * COLORS?.length)],
        size: Math.random() * 8 + 4,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8,
        shape: Math.random() > 0.5 ? 'rect' : 'circle',
        opacity: 1,
        life: 1,
      });
    }
    particlesRef.current = particles;
  }, []);

  useEffect(() => {
    if (!active) {
      cancelAnimationFrame(animRef?.current);
      particlesRef.current = [];
      const canvas = canvasRef?.current;
      if (canvas) {
        const ctx = canvas?.getContext('2d');
        ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
      }
      return;
    }
    spawnParticles();
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');

    const animate = () => {
      ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
      particlesRef.current = particlesRef?.current?.filter(p => p?.life > 0.01);
      particlesRef?.current?.forEach(p => {
        p.x += p?.vx;
        p.y += p?.vy;
        p.vy += 0.08;
        p.rotation += p?.rotationSpeed;
        p.life -= 0.008;
        p.opacity = p?.life;
        ctx?.save();
        ctx.globalAlpha = p?.opacity;
        ctx?.translate(p?.x, p?.y);
        ctx?.rotate((p?.rotation * Math.PI) / 180);
        ctx.fillStyle = p?.color;
        if (p?.shape === 'rect') {
          ctx?.fillRect(-p?.size / 2, -p?.size / 4, p?.size, p?.size / 2);
        } else {
          ctx?.beginPath();
          ctx?.arc(0, 0, p?.size / 2, 0, Math.PI * 2);
          ctx?.fill();
        }
        ctx?.restore();
      });
      if (particlesRef?.current?.length > 0) {
        animRef.current = requestAnimationFrame(animate);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef?.current);
  }, [active, spawnParticles]);

  return (
    <canvas
      ref={canvasRef}
      width={900}
      height={600}
      className="absolute inset-0 w-full h-full pointer-events-none z-50"
    />
  );
};

// ─── Stage Data ───────────────────────────────────────────────────────────────
const STAGES = [
  {
    id: 1,
    title: 'Partner Registration',
    subtitle: 'Establish the official partnership record',
    icon: 'UserPlus',
    color: 'from-violet-500 to-purple-600',
    lightColor: 'from-violet-50 to-purple-50',
    borderColor: 'border-violet-200',
    xp: 50,
    badge: { emoji: '🥇', label: 'First Step', desc: 'Completed Registration' },
    fields: [
      { id: 'companyName', label: 'Company Name', type: 'text', placeholder: 'e.g. Acme Technologies LLC', xp: 5 },
      { id: 'partnerType', label: 'Partner Type', type: 'select', options: ['Reseller', 'Distributor', 'Technology', 'Services', 'Referral'], xp: 5 },
      { id: 'country', label: 'Country / Region', type: 'text', placeholder: 'e.g. Oman', xp: 5 },
      { id: 'contactEmail', label: 'Primary Contact Email', type: 'email', placeholder: 'contact@partner.com', xp: 5 },
      { id: 'phone', label: 'Phone Number', type: 'text', placeholder: '+968 XXXX XXXX', xp: 5 },
    ],
    tip: 'Complete all fields to unlock the Profile & Documents stage.',
  },
  {
    id: 2,
    title: 'Profile & Documents',
    subtitle: 'Build a complete organizational profile',
    icon: 'FileText',
    color: 'from-blue-500 to-cyan-600',
    lightColor: 'from-blue-50 to-cyan-50',
    borderColor: 'border-blue-200',
    xp: 50,
    badge: { emoji: '📋', label: 'Document Ready', desc: 'Profile Completed' },
    fields: [
      { id: 'website', label: 'Company Website', type: 'text', placeholder: 'https://www.partner.com', xp: 5 },
      { id: 'employees', label: 'Number of Employees', type: 'select', options: ['1–10', '11–50', '51–200', '201–500', '500+'], xp: 5 },
      { id: 'revenue', label: 'Annual Revenue Range', type: 'select', options: ['< OMR 100K', 'OMR 100K–500K', 'OMR 500K–1M', '> OMR 1M'], xp: 5 },
      { id: 'description', label: 'Company Description', type: 'textarea', placeholder: 'Brief overview of your company and core business...', xp: 5 },
    ],
    tip: 'Upload documents to earn a +100 XP bonus!',
    hasUpload: true,
  },
  {
    id: 3,
    title: 'Portfolio Mapping',
    subtitle: 'Align products & services to your catalog',
    icon: 'Map',
    color: 'from-emerald-500 to-teal-600',
    lightColor: 'from-emerald-50 to-teal-50',
    borderColor: 'border-emerald-200',
    xp: 50,
    badge: { emoji: '🗺️', label: 'Mapped', desc: 'Portfolio Aligned' },
    fields: [
      { id: 'productLines', label: 'Primary Product Lines', type: 'text', placeholder: 'e.g. Cloud Solutions, Security, Networking', xp: 5 },
      { id: 'serviceAreas', label: 'Service Areas', type: 'select', options: ['IT Infrastructure', 'Cybersecurity', 'Cloud', 'Managed Services', 'Consulting', 'Training'], xp: 5 },
      { id: 'targetMarket', label: 'Target Market Segment', type: 'select', options: ['SMB', 'Mid-Market', 'Enterprise', 'Government', 'Healthcare', 'Education'], xp: 5 },
      { id: 'geographies', label: 'Covered Geographies', type: 'text', placeholder: 'e.g. GCC, MENA, Africa', xp: 5 },
    ],
    tip: 'Accurate portfolio mapping helps us match the right opportunities to you.',
  },
  {
    id: 4,
    title: 'Enablement & Training',
    subtitle: 'Certify your team for success',
    icon: 'GraduationCap',
    color: 'from-amber-500 to-orange-600',
    lightColor: 'from-amber-50 to-orange-50',
    borderColor: 'border-amber-200',
    xp: 50,
    badge: { emoji: '🎓', label: 'Certified', desc: 'Training Completed' },
    fields: [
      { id: 'certifications', label: 'Existing Certifications', type: 'text', placeholder: 'e.g. AWS, Microsoft, Cisco', xp: 5 },
      { id: 'trainingNeeds', label: 'Training Requirements', type: 'select', options: ['Sales Training', 'Technical Certification', 'Product Training', 'All of the above'], xp: 5 },
      { id: 'teamSize', label: 'Sales Team Size', type: 'select', options: ['1–5', '6–15', '16–30', '30+'], xp: 5 },
      { id: 'preferredFormat', label: 'Preferred Learning Format', type: 'select', options: ['Online Self-Paced', 'Instructor-Led', 'Hybrid', 'On-Site'], xp: 5 },
    ],
    tip: 'Certified partners get priority access to new product launches.',
  },
  {
    id: 5,
    title: 'Engagement Setup',
    subtitle: 'Configure your deal & opportunity pipeline',
    icon: 'Handshake',
    color: 'from-rose-500 to-pink-600',
    lightColor: 'from-rose-50 to-pink-50',
    borderColor: 'border-rose-200',
    xp: 50,
    badge: { emoji: '🤝', label: 'Deal Maker', desc: 'Engagement Ready' },
    fields: [
      { id: 'dealTypes', label: 'Preferred Deal Types', type: 'select', options: ['Direct Sales', 'Referral', 'Co-Sell', 'Resell', 'OEM'], xp: 5 },
      { id: 'avgDealSize', label: 'Average Deal Size', type: 'select', options: ['< OMR 10K', 'OMR 10K–50K', 'OMR 50K–200K', '> OMR 200K'], xp: 5 },
      { id: 'salesCycle', label: 'Typical Sales Cycle', type: 'select', options: ['< 1 Month', '1–3 Months', '3–6 Months', '6+ Months'], xp: 5 },
      { id: 'crm', label: 'CRM Platform Used', type: 'text', placeholder: 'e.g. Salesforce, HubSpot, Zoho', xp: 5 },
    ],
    tip: 'Partners with configured pipelines close 2x more deals on average.',
  },
  {
    id: 6,
    title: 'Performance Review',
    subtitle: 'Set targets and review criteria',
    icon: 'BarChart3',
    color: 'from-indigo-500 to-blue-600',
    lightColor: 'from-indigo-50 to-blue-50',
    borderColor: 'border-indigo-200',
    xp: 50,
    badge: { emoji: '⭐', label: 'Performer', desc: 'KPIs Configured' },
    fields: [
      { id: 'annualTarget', label: 'Annual Revenue Target (OMR)', type: 'text', placeholder: 'e.g. 500,000', xp: 5 },
      { id: 'kpiCadence', label: 'Review Cadence', type: 'select', options: ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'], xp: 5 },
      { id: 'primaryKpi', label: 'Primary KPI Focus', type: 'select', options: ['Revenue Growth', 'New Logos', 'Customer Retention', 'Market Expansion'], xp: 5 },
      { id: 'escalationContact', label: 'Escalation Contact', type: 'email', placeholder: 'manager@partner.com', xp: 5 },
    ],
    tip: 'Setting clear targets unlocks the final Active Partner status.',
  },
  {
    id: 7,
    title: 'Active Partner Status',
    subtitle: 'Final review and activation',
    icon: 'Trophy',
    color: 'from-yellow-500 to-amber-600',
    lightColor: 'from-yellow-50 to-amber-50',
    borderColor: 'border-yellow-200',
    xp: 100,
    badge: { emoji: '🏆', label: 'Active Partner', desc: 'Lifecycle Complete' },
    fields: [
      { id: 'tier', label: 'Assigned Partner Tier', type: 'select', options: ['Bronze', 'Silver', 'Gold', 'Platinum'], xp: 5 },
      { id: 'accountManager', label: 'Assigned Account Manager', type: 'text', placeholder: 'Full name', xp: 5 },
      { id: 'activationDate', label: 'Activation Date', type: 'date', placeholder: '', xp: 5 },
      { id: 'notes', label: 'Activation Notes', type: 'textarea', placeholder: 'Any special terms, conditions, or notes...', xp: 5 },
    ],
    tip: 'Completing this step makes the partner officially active in the system.',
  },
];

const TOTAL_XP = STAGES?.reduce((sum, s) => sum + s?.xp + s?.fields?.length * s?.fields?.[0]?.xp, 0);

// ─── XP Counter ───────────────────────────────────────────────────────────────
const XPCounter = ({ xp, prevXp }) => {
  const [display, setDisplay] = useState(prevXp);
  useEffect(() => {
    if (display === xp) return;
    const step = xp > display ? 1 : -1;
    const diff = Math.abs(xp - display);
    const increment = Math.max(1, Math.floor(diff / 20));
    const timer = setInterval(() => {
      setDisplay(prev => {
        const next = prev + step * increment;
        if ((step > 0 && next >= xp) || (step < 0 && next <= xp)) {
          clearInterval(timer);
          return xp;
        }
        return next;
      });
    }, 16);
    return () => clearInterval(timer);
  }, [xp]);
  return <span>{display}</span>;
};

// ─── Badge Popup ──────────────────────────────────────────────────────────────
const BadgePopup = ({ badge, visible, onDismiss }) => {
  useEffect(() => {
    if (visible) {
      const t = setTimeout(onDismiss, 3500);
      return () => clearTimeout(t);
    }
  }, [visible, onDismiss]);

  return (
    <div
      className={`fixed right-8 top-24 z-[10000] transition-all duration-500 ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-amber-200 p-4 flex items-center gap-3 min-w-[220px]">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-2xl shadow-md">
          {badge?.emoji}
        </div>
        <div>
          <div className="text-xs font-bold text-amber-600 uppercase tracking-wider">Badge Earned!</div>
          <div className="text-sm font-bold text-slate-800">{badge?.label}</div>
          <div className="text-xs text-slate-500">{badge?.desc}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Wizard ──────────────────────────────────────────────────────────────
const PartnerLifecycleWizard = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0-indexed
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const [xp, setXp] = useState(0);
  const [prevXp, setPrevXp] = useState(0);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [badgePopup, setBadgePopup] = useState(null);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [stepAnim, setStepAnim] = useState('enter');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedPartnerId, setSavedPartnerId] = useState(null);
  const fileInputRef = useRef(null);

  const stage = STAGES?.[currentStep];
  const totalSteps = STAGES?.length;
  const progressPct = Math.round(((completedSteps?.length) / totalSteps) * 100);

  // XP level calculation
  const level = Math.floor(xp / 100) + 1;
  const xpInLevel = xp % 100;
  const xpToNextLevel = 100 - xpInLevel;

  const handleFieldChange = (fieldId, value, fieldXp) => {
    const wasEmpty = !formData?.[`${currentStep}_${fieldId}`];
    setFormData(prev => ({ ...prev, [`${currentStep}_${fieldId}`]: value }));
    if (wasEmpty && value) {
      setPrevXp(xp);
      setXp(prev => prev + fieldXp);
    }
  };

  const getFieldValue = (fieldId) => formData?.[`${currentStep}_${fieldId}`] || '';

  const isStepFilled = () => {
    return stage?.fields?.every(f => !!getFieldValue(f?.id));
  };

  // ── Save all wizard data to Supabase ──────────────────────────────────────
  const saveToSupabase = async (allFormData, allUploadedFiles) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Step 1 fields: companyName, partnerType, country, contactEmail, phone
      // Step 2 fields: website, employees, revenue, description
      // Step 3 fields: productLines, serviceAreas, targetMarket, geographies
      // Step 4 fields: certifications, trainingNeeds, teamSize, preferredFormat
      // Step 5 fields: dealTypes, avgDealSize, salesCycle, crm
      // Step 6 fields: annualTarget, kpiCadence, primaryKpi, escalationContact
      // Step 7 fields: tier, accountManager, activationDate, notes

      const get = (stepIdx, fieldId) => allFormData?.[`${stepIdx}_${fieldId}`] || '';

      // Map tier from wizard to DB enum
      const tierMap = { Bronze: 'Silver', Silver: 'Silver', Gold: 'Gold', Platinum: 'Strategic' };
      const rawTier = get(6, 'tier');
      const dbTier = tierMap?.[rawTier] || 'Silver';

      // Map partner type to DB enum
      const typeMap = {
        Reseller: 'Reseller', Distributor: 'Distributor',
        Technology: 'ISV', Services: 'MSP', Referral: 'Referral',
      };
      const rawType = get(0, 'partnerType');
      const dbType = typeMap?.[rawType] || 'Reseller';

      // Build partner record
      const partnerPayload = {
        legal_name: get(0, 'companyName'),
        brand_name: get(0, 'companyName'),
        partner_type: dbType,
        country: get(0, 'country'),
        website: get(1, 'website'),
        tier: dbTier,
        status: 'approved',
        health_score: 75,
        partnership_start_date: get(6, 'activationDate') || new Date()?.toISOString()?.split('T')?.[0],
        authorized_signatory: get(6, 'accountManager'),
        agreement_status: 'signed',
        ownership_structure: get(1, 'employees'),
        financial_risk_level: 'low',
      };

      // Create partner
      const partner = await partnerService?.create(partnerPayload);
      const partnerId = partner?.id;

      // Save primary contact from Step 1
      if (get(0, 'contactEmail')) {
        await contactsService?.create({
          partner_id: partnerId,
          name: get(6, 'accountManager') || get(0, 'companyName') + ' Contact',
          role: 'Primary Contact',
          email: get(0, 'contactEmail'),
          phone: get(0, 'phone'),
          is_primary: true,
        });
      }

      // Save escalation contact from Step 6
      if (get(5, 'escalationContact') && get(5, 'escalationContact') !== get(0, 'contactEmail')) {
        await contactsService?.create({
          partner_id: partnerId,
          name: 'Escalation Contact',
          role: 'Escalation',
          email: get(5, 'escalationContact'),
          is_primary: false,
        });
      }

      // Save certifications from Step 4
      const certText = get(3, 'certifications');
      if (certText) {
        const certs = certText?.split(',')?.map(c => c?.trim())?.filter(Boolean);
        for (const cert of certs) {
          await supabase?.from('partner_certifications')?.insert({
            partner_id: partnerId,
            name: cert,
            issuer: cert,
            cert_status: 'active',
          });
        }
      }

      // Save training from Step 4
      const trainingNeeds = get(3, 'trainingNeeds');
      if (trainingNeeds) {
        await supabase?.from('partner_trainings')?.insert({
          partner_id: partnerId,
          name: trainingNeeds,
          description: `Preferred format: ${get(3, 'preferredFormat')}`,
          duration: '40 hours',
          participants: parseInt(get(3, 'teamSize')?.split('–')?.[0] || '1', 10),
          training_status: 'required',
          progress: 0,
        });
      }

      // Save performance metric from Step 6
      const annualTarget = get(5, 'annualTarget');
      if (annualTarget) {
        await supabase?.from('performance_metrics')?.insert({
          partner_id: partnerId,
          metric_month: new Date()?.toISOString()?.split('T')?.[0],
          target_revenue: parseFloat(annualTarget?.replace(/,/g, '') || '0'),
          revenue: 0,
          win_rate: 0,
          sla_compliance: 0,
          delivery_quality: 0,
          client_feedback: 0,
        });
      }

      // Save opportunity from Step 5
      const dealType = get(4, 'dealTypes');
      if (dealType) {
        await supabase?.from('opportunities')?.insert({
          partner_id: partnerId,
          name: `${get(0, 'companyName')} — Initial Opportunity`,
          sector: get(2, 'targetMarket') || 'Enterprise',
          role: dealType,
          opp_status: 'Identified',
          win_probability: 50,
          description: `CRM: ${get(4, 'crm')} | Avg Deal: ${get(4, 'avgDealSize')} | Cycle: ${get(4, 'salesCycle')}`,
        });
      }

      setSavedPartnerId(partnerId);
      onComplete?.();
      return partnerId;
    } catch (err) {
      console.error('Wizard save error:', err);
      setSaveError(err?.message || 'Failed to save partner data. Please try again.');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (!isStepFilled()) return;
    const stepIdx = currentStep;

    // Mark step complete
    if (!completedSteps?.includes(stepIdx)) {
      setCompletedSteps(prev => [...prev, stepIdx]);
      setPrevXp(xp);
      setXp(prev => prev + stage?.xp);

      // Badge
      const badge = stage?.badge;
      if (!earnedBadges?.find(b => b?.label === badge?.label)) {
        setEarnedBadges(prev => [...prev, badge]);
        setBadgePopup(badge);
        setShowBadgePopup(true);
      }

      // Confetti
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }

    if (currentStep < totalSteps - 1) {
      setStepAnim('exit');
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setStepAnim('enter');
      }, 250);
    } else {
      // Final step — save to Supabase then show completion
      const allCompleted = [...completedSteps, stepIdx];
      setCompletedSteps(allCompleted);
      const pid = await saveToSupabase(formData, uploadedFiles);
      if (pid) {
        setTimeout(() => setIsComplete(true), 300);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 0) return;
    setStepAnim('exit-back');
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setStepAnim('enter-back');
    }, 250);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setCurrentStep(0);
      setCompletedSteps([]);
      setFormData({});
      setXp(0);
      setPrevXp(0);
      setEarnedBadges([]);
      setShowConfetti(false);
      setIsComplete(false);
      setUploadedFiles([]);
      setSavedPartnerId(null);
      setSaveError(null);
    }, 400);
  };

  const handleViewProfile = () => {
    handleClose();
    if (savedPartnerId) {
      navigate(`/partner-profile-portal/${savedPartnerId}`);
    } else {
      navigate('/all-partners');
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files);
    if (files?.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
      setPrevXp(xp);
      setXp(prev => prev + 100);
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
      setPrevXp(xp);
      setXp(prev => prev + 100);
    }
  };

  const animClass = {
    enter: 'animate-slide-in-right',
    'enter-back': 'animate-slide-in-left',
    exit: 'animate-slide-out-left',
    'exit-back': 'animate-slide-out-right',
  }?.[stepAnim] || '';

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInLeft  { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideOutLeft { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-40px); } }
        @keyframes slideOutRight{ from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(40px); } }
        @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes pulse-ring { 0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); } 50% { box-shadow: 0 0 0 8px rgba(99,102,241,0); } }
        @keyframes xpFloat { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-30px); } }
        .animate-slide-in-right  { animation: slideInRight 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .animate-slide-in-left   { animation: slideInLeft  0.3s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .animate-slide-out-left  { animation: slideOutLeft  0.25s ease-in forwards; }
        .animate-slide-out-right { animation: slideOutRight 0.25s ease-in forwards; }
        .animate-pop-in          { animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .pulse-ring               { animation: pulse-ring 2s ease-in-out infinite; }
        .wizard-scrollbar::-webkit-scrollbar { width: 4px; }
        .wizard-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .wizard-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
      `}</style>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9000] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => e?.target === e?.currentTarget && handleClose()}
      >
        {/* Modal */}
        <div className="relative w-full max-w-5xl h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
          <ConfettiCanvas active={showConfetti} />

          {/* ── Header Bar ── */}
          <div className="relative flex-none bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <Icon name="Zap" size={18} className="text-white" />
                </div>
                <div>
                  <h1 className="text-white font-bold text-base leading-tight font-heading">Partner Lifecycle Wizard</h1>
                  <p className="text-slate-400 text-xs">Complete all 7 stages to activate your partner</p>
                </div>
              </div>

              {/* XP Display */}
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-2 bg-slate-700/60 rounded-xl px-3 py-1.5">
                  <span className="text-amber-400 text-sm">⚡</span>
                  <span className="text-white font-bold text-sm">
                    <XPCounter xp={xp} prevXp={prevXp} /> XP
                  </span>
                </div>
                <div className="hidden sm:flex items-center gap-2 bg-slate-700/60 rounded-xl px-3 py-1.5">
                  <span className="text-purple-400 text-sm">🏅</span>
                  <span className="text-white font-bold text-sm">{earnedBadges?.length} Badges</span>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 rounded-lg bg-slate-700/60 hover:bg-slate-600 flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={16} className="text-slate-300" />
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-400 font-medium">{progressPct}% Complete</span>
                <span className="text-xs text-slate-400">{completedSteps?.length}/{totalSteps} stages done</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* ── Roadmap Steps ── */}
          <div className="flex-none bg-slate-50 border-b border-slate-200 px-4 py-3 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max mx-auto justify-center">
              {STAGES?.map((s, idx) => {
                const isDone = completedSteps?.includes(idx);
                const isCurrent = idx === currentStep && !isComplete;
                const isLocked = !isDone && !isCurrent && idx > (completedSteps?.length);
                return (
                  <React.Fragment key={s?.id}>
                    <button
                      onClick={() => {
                        if (!isLocked && !isComplete) {
                          setStepAnim(idx > currentStep ? 'enter' : 'enter-back');
                          setCurrentStep(idx);
                        }
                      }}
                      disabled={isLocked || isComplete}
                      className={`relative flex flex-col items-center gap-1 px-2 py-1.5 rounded-xl transition-all duration-200 min-w-[72px] ${
                        isCurrent ? 'bg-white shadow-md' : isDone ? 'hover:bg-white/70' : isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/70 cursor-pointer'
                      }`}
                    >
                      <div className={`relative w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                        isDone
                          ? 'bg-emerald-500 text-white shadow-md'
                          : isCurrent
                          ? `bg-gradient-to-br ${s?.color} text-white shadow-md pulse-ring`
                          : 'bg-slate-200 text-slate-500'
                      }`}>
                        {isDone ? <Icon name="Check" size={14} /> : isLocked ? <Icon name="Lock" size={12} /> : s?.id}
                      </div>
                      <span className={`text-[10px] font-semibold leading-tight text-center max-w-[68px] ${
                        isCurrent ? 'text-slate-800' : isDone ? 'text-emerald-600' : 'text-slate-400'
                      }`}>
                        {s?.title?.split(' ')?.slice(0, 2)?.join(' ')}
                      </span>
                    </button>
                    {idx < STAGES?.length - 1 && (
                      <div className={`w-6 h-0.5 rounded-full flex-none transition-all duration-500 ${
                        completedSteps?.includes(idx) ? 'bg-emerald-400' : 'bg-slate-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* ── Content Area ── */}
          <div className="flex-1 overflow-hidden relative">
            {isSaving ? (
              /* ── Saving Screen ── */
              (<div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-6"></div>
                <h2 className="text-xl font-bold text-slate-900 font-heading mb-2">Saving Partner Data...</h2>
                <p className="text-slate-500 text-sm">Creating partner profile and saving all information to the system.</p>
              </div>)
            ) : saveError ? (
              /* ── Error Screen ── */
              (<div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="text-5xl mb-4">⚠️</div>
                <h2 className="text-xl font-bold text-slate-900 font-heading mb-2">Save Failed</h2>
                <p className="text-red-600 text-sm mb-6 max-w-md">{saveError}</p>
                <button
                  onClick={() => saveToSupabase(formData, uploadedFiles)?.then(pid => pid && setTimeout(() => setIsComplete(true), 300))}
                  className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg"
                >
                  Try Again
                </button>
              </div>)
            ) : isComplete ? (
              /* ── Completion Screen ── */
              (<div className="h-full flex flex-col items-center justify-center p-8 text-center animate-pop-in">
                <div className="text-7xl mb-4">🏆</div>
                <h2 className="text-3xl font-bold text-slate-900 font-heading mb-2">Congratulations!</h2>
                <p className="text-slate-500 text-lg mb-2">
                  <span className="font-bold text-slate-800">{formData?.['0_companyName'] || 'The Partner'}</span> is now an <span className="font-bold text-emerald-600">Active Partner</span>
                </p>
                <p className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-6 flex items-center gap-1.5 mx-auto w-fit">
                  <Icon name="CheckCircle" size={12} /> Partner profile saved to Supabase
                </p>
                {/* Stats */}
                <div className="flex items-center gap-6 mb-8">
                  <div className="flex flex-col items-center bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4">
                    <span className="text-3xl font-bold text-amber-600">{xp}</span>
                    <span className="text-xs text-amber-500 font-semibold mt-1">⚡ Total XP</span>
                  </div>
                  <div className="flex flex-col items-center bg-purple-50 border border-purple-200 rounded-2xl px-6 py-4">
                    <span className="text-3xl font-bold text-purple-600">{earnedBadges?.length}</span>
                    <span className="text-xs text-purple-500 font-semibold mt-1">🏅 Badges</span>
                  </div>
                  <div className="flex flex-col items-center bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-4">
                    <span className="text-3xl font-bold text-emerald-600">7/7</span>
                    <span className="text-xs text-emerald-500 font-semibold mt-1">✅ Stages</span>
                  </div>
                </div>
                {/* Badges earned */}
                <div className="flex flex-wrap gap-2 justify-center mb-8">
                  {earnedBadges?.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
                      <span className="text-xl">{b?.emoji}</span>
                      <span className="text-xs font-semibold text-slate-700">{b?.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleViewProfile}
                    className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    View Partner Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate('/all-partners');
                      handleClose();
                    }}
                    className="px-6 py-3 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                  >
                    View All Partners
                  </button>
                  <button
                    onClick={() => {
                      setCurrentStep(0);
                      setCompletedSteps([]);
                      setFormData({});
                      setXp(0);
                      setPrevXp(0);
                      setEarnedBadges([]);
                      setIsComplete(false);
                      setUploadedFiles([]);
                      setSavedPartnerId(null);
                      setSaveError(null);
                    }}
                    className="px-6 py-3 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                  >
                    Start Another Journey
                  </button>
                </div>
              </div>)
            ) : (
              /* ── Step Content ── */
              (<div className={`h-full flex flex-col overflow-hidden ${animClass}`}>
                <div className="flex-1 overflow-y-auto wizard-scrollbar">
                  <div className="p-6 max-w-2xl mx-auto">
                    {/* Step Header */}
                    <div className={`flex items-start gap-4 mb-6 p-4 rounded-2xl bg-gradient-to-br ${stage?.lightColor} border ${stage?.borderColor}`}>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stage?.color} flex items-center justify-center shadow-md flex-none`}>
                        <Icon name={stage?.icon} size={22} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Stage {stage?.id} of {totalSteps}</span>
                          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">+{stage?.xp} XP on complete</span>
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 font-heading">{stage?.title}</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{stage?.subtitle}</p>
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                      {stage?.fields?.map((field) => (
                        <div key={field?.id} className="group">
                          <label className="flex items-center justify-between text-sm font-semibold text-slate-700 mb-1.5">
                            <span>{field?.label}</span>
                            {!getFieldValue(field?.id) && (
                              <span className="text-xs text-violet-500 font-medium">+{field?.xp} XP</span>
                            )}
                            {getFieldValue(field?.id) && (
                              <span className="text-xs text-emerald-500 font-medium flex items-center gap-1">
                                <Icon name="CheckCircle" size={12} /> Earned
                              </span>
                            )}
                          </label>
                          {field?.type === 'select' ? (
                            <select
                              value={getFieldValue(field?.id)}
                              onChange={e => handleFieldChange(field?.id, e?.target?.value, field?.xp)}
                              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all hover:border-slate-300"
                            >
                              <option value="">Select an option...</option>
                              {field?.options?.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : field?.type === 'textarea' ? (
                            <textarea
                              value={getFieldValue(field?.id)}
                              onChange={e => handleFieldChange(field?.id, e?.target?.value, field?.xp)}
                              placeholder={field?.placeholder}
                              rows={3}
                              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all hover:border-slate-300 resize-none"
                            />
                          ) : (
                            <input
                              type={field?.type}
                              value={getFieldValue(field?.id)}
                              onChange={e => handleFieldChange(field?.id, e?.target?.value, field?.xp)}
                              placeholder={field?.placeholder}
                              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all hover:border-slate-300"
                            />
                          )}
                        </div>
                      ))}

                      {/* Document Upload (Stage 2) */}
                      {stage?.hasUpload && (
                        <div>
                          <label className="flex items-center justify-between text-sm font-semibold text-slate-700 mb-1.5">
                            <span>📎 Upload Documents</span>
                            <span className="text-xs text-amber-500 font-medium">+100 XP bonus</span>
                          </label>
                          <div
                            onDragOver={e => { e?.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef?.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
                              isDragging
                                ? 'border-violet-400 bg-violet-50'
                                : 'border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-violet-50/50'
                            }`}
                          >
                            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileInput} />
                            <Icon name="Upload" size={24} className="text-slate-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-slate-600">Drag & drop files here</p>
                            <p className="text-xs text-slate-400 mt-1">or click to browse — PDF, DOC, JPG supported</p>
                            {uploadedFiles?.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2 justify-center">
                                {uploadedFiles?.map((f, i) => (
                                  <span key={i} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg font-medium">
                                    ✓ {f?.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Tip */}
                      <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl p-3">
                        <span className="text-blue-500 text-base flex-none mt-0.5">💡</span>
                        <p className="text-xs text-blue-700 font-medium leading-relaxed">{stage?.tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* ── Footer ── */}
                <div className="flex-none border-t border-slate-100 bg-white px-6 py-4">
                  <div className="max-w-2xl mx-auto">
                    {/* Level Bar */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold text-slate-500 whitespace-nowrap">Level {level}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
                          style={{ width: `${xpInLevel}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">{xpToNextLevel} XP to Level {level + 1}</span>
                    </div>

                    {/* Nav Buttons */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <Icon name="ChevronLeft" size={16} /> Back
                      </button>

                      <div className="flex items-center gap-2">
                        {!isStepFilled() && (
                          <span className="text-xs text-slate-400 hidden sm:block">Fill all fields to continue</span>
                        )}
                        <button
                          onClick={handleNext}
                          disabled={!isStepFilled() || isSaving}
                          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg ${
                            isStepFilled() && !isSaving
                              ? `bg-gradient-to-r ${stage?.color} text-white hover:opacity-90`
                              : 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                          }`}
                        >
                          {currentStep === totalSteps - 1 ? 'Complete Journey' : 'Continue'}
                          <span className="text-xs font-bold opacity-80">+{stage?.xp} XP</span>
                          <Icon name={currentStep === totalSteps - 1 ? 'Trophy' : 'ChevronRight'} size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>)
            )}
          </div>
        </div>
      </div>
      {/* Badge Popup */}
      <BadgePopup
        badge={badgePopup}
        visible={showBadgePopup}
        onDismiss={() => setShowBadgePopup(false)}
      />
    </>
  );
};

export default PartnerLifecycleWizard;
